/**
 * Analytics Tracker for Kashkaval Catering
 * Integrates with kashkavalgarden.com/track-impression tracking server
 */

(function() {
  'use strict';

  // Configuration
  const TRACKER_URL = 'https://kashkavalgarden.com';
  const IMPRESSION_ENDPOINT = '/track-impression';
  const CLICK_ENDPOINT = '/track-impression'; // Use track-impression with event data for clicks
  const CONVERSION_ENDPOINT = '/track-conversion';

  class AnalyticsTracker {
    constructor() {
      this.sessionId = this.getOrCreateSessionId();
      this.pageLoadTime = Date.now();
    }

    /**
     * Get or create a session ID (stored in sessionStorage)
     */
    getOrCreateSessionId() {
      let sessionId = sessionStorage.getItem('analytics_session_id');
      if (!sessionId) {
        sessionId = 'sess_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
        sessionStorage.setItem('analytics_session_id', sessionId);
      }
      return sessionId;
    }

    /**
     * Get UTM parameters from URL
     */
    getUTMParams() {
      const urlParams = new URLSearchParams(window.location.search);
      return {
        utm_source: urlParams.get('utm_source') || '',
        utm_medium: urlParams.get('utm_medium') || '',
        utm_campaign: urlParams.get('utm_campaign') || '',
        utm_term: urlParams.get('utm_term') || '',
        utm_content: urlParams.get('utm_content') || '',
        gclid: urlParams.get('gclid') || '',
        device: urlParams.get('device') || '',
        network: urlParams.get('network') || '',
        matchtype: urlParams.get('matchtype') || '',
        adgroup: urlParams.get('adgroup') || '',
        placement: urlParams.get('placement') || '',
        target: urlParams.get('target') || '',
        loc: urlParams.get('loc') || ''
      };
    }

    /**
     * Get page type based on URL
     */
    getPageType() {
      const path = window.location.pathname;
      if (path === '/' || path === '/index' || path === '/index.html') return 'homepage';
      if (path.includes('/collections/')) return 'collection';
      if (path.includes('/products/')) return 'product';
      if (path.includes('/cart')) return 'cart';
      if (path.includes('/checkout')) return 'checkout';
      if (path.includes('/pages/contact')) return 'contact';
      if (path.includes('/pages/party-packages')) return 'party-packages';
      if (path.includes('/pages/')) return 'page';
      return 'other';
    }

    /**
     * Build tracking pixel URL
     */
    buildPixelURL(endpoint, additionalParams = {}) {
      const params = {
        ...this.getUTMParams(),
        url: window.location.href,
        page_title: document.title,
        page_type: this.getPageType(),
        ref: document.referrer,
        session_id: this.sessionId,
        timestamp: Date.now(),
        ...additionalParams
      };

      // Remove empty parameters
      Object.keys(params).forEach(key => {
        if (!params[key]) delete params[key];
      });

      const queryString = Object.keys(params)
        .map(key => encodeURIComponent(key) + '=' + encodeURIComponent(params[key]))
        .join('&');

      return TRACKER_URL + endpoint + '?' + queryString;
    }

    /**
     * Track page impression
     */
    trackImpression() {
      const pixelURL = this.buildPixelURL(IMPRESSION_ENDPOINT);

      // Use image pixel for tracking
      const img = new Image(1, 1);
      img.src = pixelURL;
      img.style.display = 'none';
      document.body.appendChild(img);

      console.log('[Analytics] Page impression tracked:', this.getPageType());
    }

    /**
     * Track click event
     */
    trackClick(eventName, data = {}) {
      const pixelURL = this.buildPixelURL(CLICK_ENDPOINT, {
        event_name: eventName,
        click_text: data.text || '',
        click_href: data.href || '',
        click_target: data.target || '',
        ...data
      });

      // Use image pixel for tracking (GET request)
      const img = new Image(1, 1);
      img.src = pixelURL;
      img.style.display = 'none';

      console.log('[Analytics] Click tracked:', eventName, data);
    }

    /**
     * Track conversion event
     */
    trackConversion(conversionType, data = {}) {
      const pixelURL = this.buildPixelURL(CONVERSION_ENDPOINT, {
        conversion_type: conversionType,
        ...data
      });

      // Use image pixel for tracking (GET request)
      const img = new Image(1, 1);
      img.src = pixelURL;
      img.style.display = 'none';

      console.log('[Analytics] Conversion tracked:', conversionType, data);
    }

    /**
     * Set up automatic tracking
     */
    init() {
      // Track page impression on load
      this.trackImpression();

      // Track time on page (send after 10 seconds)
      setTimeout(() => {
        this.trackClick('time_on_page', { duration: 10 });
      }, 10000);

      // Track Add to Cart button clicks
      this.trackAddToCartClicks();

      // Track navigation clicks
      this.trackNavigationClicks();

      // Track external links
      this.trackExternalLinks();

      // Track form submissions
      this.trackFormSubmissions();

      // Track checkout events
      this.trackCheckoutEvents();
    }

    /**
     * Track Add to Cart button clicks
     */
    trackAddToCartClicks() {
      document.addEventListener('click', (e) => {
        const addToCartBtn = e.target.closest('[data-product-id]');
        if (addToCartBtn) {
          const productId = addToCartBtn.getAttribute('data-product-id');
          const productHandle = addToCartBtn.getAttribute('data-product-handle');

          this.trackClick('add_to_cart', {
            product_id: productId,
            product_handle: productHandle
          });
        }
      });
    }

    /**
     * Track navigation clicks
     */
    trackNavigationClicks() {
      // Track collection navigation
      document.addEventListener('click', (e) => {
        const collectionLink = e.target.closest('.collection-nav__item');
        if (collectionLink) {
          const collectionName = collectionLink.textContent.trim();
          this.trackClick('collection_nav', {
            text: collectionName,
            href: collectionLink.href
          });
        }
      });

      // Track announcement bar link
      document.addEventListener('click', (e) => {
        const announcementLink = e.target.closest('.announcement-bar__link');
        if (announcementLink) {
          this.trackClick('announcement_bar', {
            text: announcementLink.textContent.trim(),
            href: announcementLink.href
          });
        }
      });
    }

    /**
     * Track external links
     */
    trackExternalLinks() {
      document.addEventListener('click', (e) => {
        const link = e.target.closest('a[href]');
        if (link && link.href) {
          const isExternal = link.hostname !== window.location.hostname;
          if (isExternal) {
            this.trackClick('external_link', {
              text: link.textContent.trim(),
              href: link.href,
              target: link.getAttribute('target')
            });
          }
        }
      });
    }

    /**
     * Track form submissions
     */
    trackFormSubmissions() {
      document.addEventListener('submit', (e) => {
        const form = e.target;
        if (form.tagName === 'FORM') {
          const formName = form.name || form.id || 'unnamed_form';
          this.trackConversion('form_submit', {
            form_name: formName,
            form_action: form.action
          });
        }
      });
    }

    /**
     * Track checkout events
     */
    trackCheckoutEvents() {
      // Track when checkout button is clicked
      document.addEventListener('click', (e) => {
        const checkoutBtn = e.target.closest('[name="checkout"]');
        if (checkoutBtn) {
          this.trackConversion('checkout_initiated', {
            cart_total: this.getCartTotal()
          });
        }
      });
    }

    /**
     * Get cart total (if available in the page)
     */
    getCartTotal() {
      const totalElement = document.querySelector('.cart__subtotal-price, .cart-drawer__total-price');
      if (totalElement) {
        return totalElement.textContent.trim();
      }
      return '';
    }
  }

  // Initialize tracker when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      window.analyticsTracker = new AnalyticsTracker();
      window.analyticsTracker.init();
    });
  } else {
    window.analyticsTracker = new AnalyticsTracker();
    window.analyticsTracker.init();
  }

  // Expose tracker globally for manual tracking
  window.trackEvent = function(eventName, data) {
    if (window.analyticsTracker) {
      window.analyticsTracker.trackClick(eventName, data);
    }
  };

  window.trackConversion = function(conversionType, data) {
    if (window.analyticsTracker) {
      window.analyticsTracker.trackConversion(conversionType, data);
    }
  };
})();
